import { Link } from "react-router-dom";

export default function Landing() {
  return (
    <main>
      {/* ---------------- HERO ---------------- */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-12 sm:pt-20 pb-24">
        <div className="grid grid-cols-12 gap-6 items-center">
          <div className="col-span-12 lg:col-span-6 animate-fade-up">
            <p className="label-overline mb-6">Est. 2026 · A modern courtesy revived</p>
            <h1 className="font-display text-5xl sm:text-7xl leading-[0.95] tracking-tight">
              Of <em className="text-wax-red">hearths</em>,
              <br /> of <em>hermits</em>,
              <br /> of quiet arrangement.
            </h1>
            <p className="mt-8 font-body text-lg text-ink-iron max-w-xl">
              In another century, a wealthy landowner kept an ornamental hermit upon the
              property — to walk the grounds, to seem profound, to never quite be seen.
              We are bringing the practice back. With less powdered wig, more dignified housing.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/sign-up" data-testid="hero-cta-begin" className="wax-button animate-flicker">Begin the Arrangement</Link>
              <Link to="/manifesto" data-testid="hero-cta-manifesto" className="ghost-button">Read the Manifesto</Link>
            </div>
            <p className="mt-12 marginalia max-w-md">
              Hosts seek a presence. Hermits seek a roof. Rent is not always currency.
            </p>
          </div>

          <div className="col-span-12 lg:col-span-6 relative lg:-mt-10">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1767537551488-770e15b15173"
                alt="A mossy stone cottage upon a hill"
                className="w-full h-[460px] sm:h-[560px] object-cover border border-rule"
              />
              <div className="absolute -bottom-6 -left-6 hidden sm:block bg-parchment-faded border border-rule px-5 py-4 max-w-[260px]">
                <p className="label-overline">Letter from a Cotswolds host</p>
                <p className="mt-2 font-body italic text-sm text-ink-iron">
                  “Seeking a quiet soul to tend the herb garden. Letters welcome. Conversation rare.”
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="fleuron my-6">❦ ❦ ❦</div>

      {/* ---------------- HOW IT WORKS ---------------- */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
        <div className="grid grid-cols-12 gap-6 mb-12">
          <div className="col-span-12 md:col-span-6">
            <p className="label-overline mb-3">The Arrangement</p>
            <h2 className="font-display text-4xl sm:text-5xl">A bargain, plainly struck.</h2>
          </div>
          <div className="col-span-12 md:col-span-6">
            <p className="font-body text-ink-iron text-lg">
              No rent. No algorithms shouting. Just a host with land and a hermit with the
              constitution to be left alone. Terms are honestly written. Letters are honestly sent.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              n: "I.",
              title: "Write thy profile",
              body: "Hermits compose a letter of self. Hosts describe the land and the terms. Both parties speak plain.",
            },
            {
              n: "II.",
              title: "Browse or be sought",
              body: "Walk amongst the listings. A mossy cabin. A walled garden. A wandering soul with a small dog.",
            },
            {
              n: "III.",
              title: "Send a sealed letter",
              body: "Submit a letter of intent. Correspond as you would in 1782 — slowly, with care, and on parchment.",
            },
          ].map((s) => (
            <div key={s.n} className="parchment-card">
              <p className="font-display italic text-wax-red text-3xl mb-3">{s.n}</p>
              <p className="font-display text-2xl mb-2">{s.title}</p>
              <p className="font-body text-ink-iron">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- FEATURED LISTING ---------------- */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-5">
            <img
              src="https://images.unsplash.com/photo-1510568192-5cd6e7a47edd"
              alt="A candlelit interior"
              className="w-full h-[520px] object-cover border border-rule"
            />
          </div>
          <div className="col-span-12 lg:col-span-7 lg:pl-10 flex flex-col justify-center">
            <p className="label-overline mb-4">From the listings</p>
            <h3 className="font-display text-4xl sm:text-5xl mb-6">
              <em>“A roof of stone, a kettle of tea, and the loud silence of a forest.”</em>
            </h3>
            <p className="font-body text-ink-iron text-lg max-w-2xl">
              Our hosts come from old stone walls in Yorkshire and from off-grid cabins in
              the Adirondacks. Our hermits come from libraries and long highways. We pair
              them with the patience of a postman in an era before the post was anything but slow.
            </p>
            <div className="mt-8 flex gap-4">
              <Link to="/browse/hermitages" data-testid="featured-browse-hermitages" className="wax-button">Browse Hermitages</Link>
              <Link to="/browse/hermits" data-testid="featured-browse-hermits" className="ghost-button">Browse Hermits</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- PRICING TEASER ---------------- */}
      <section className="bg-parchment-bone border-y border-rule py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="text-center mb-12">
            <p className="label-overline">Four ledgers, four arrangements</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-2">Choose thy subscription</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { name: "Free", price: "—", note: "Walk amongst us. Browse, observe, begin." },
              { name: "Starter", price: "$1", note: "$1 for 7 days, then $4.99/mo. Try the arrangement." },
              { name: "Standard", price: "$4.99", note: "Per month. Full correspondence privileges." },
              { name: "Annual", price: "$35.99", note: "Per year. Save 40%. For the patient." },
            ].map((t, i) => (
              <div key={t.name} className={`parchment-card ${i === 3 ? "border-wax-red" : ""}`}>
                <p className="label-overline">{t.name}</p>
                <p className="font-display text-4xl mt-3">{t.price}</p>
                <p className="font-body text-sm text-ink-iron mt-4">{t.note}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/pricing" data-testid="pricing-teaser-cta" className="wax-button">See full ledger</Link>
          </div>
        </div>
      </section>

      {/* ---------------- CLOSING ---------------- */}
      <section className="max-w-3xl mx-auto px-6 sm:px-10 py-24 text-center">
        <div className="fleuron mb-6">❦</div>
        <h2 className="font-display text-4xl sm:text-5xl">
          <em>Let the hermitage begin.</em>
        </h2>
        <p className="mt-6 font-body text-lg text-ink-iron">
          A small rebellion. A quieter life. A roof, kindly given.
        </p>
        <div className="mt-10 flex justify-center gap-4">
          <Link to="/sign-up" data-testid="closing-cta-signup" className="wax-button">Take the Vow</Link>
        </div>
      </section>
    </main>
  );
}
